'use client';

import { useEffect, useCallback, useMemo, useRef } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { userAtom, shouldDisconnectWalletAtom } from '@/store';
import toast from 'react-hot-toast';
import { useTranslations } from 'next-intl';
import { usePostWeb3Login, CustomWeb3LoginRes } from '@/features/auth/services/web3-login';
import { usePostThirdLogin, CustomThirdLoginRes } from '@/features/auth/services/third-login';
import { UseCompleteRegistration } from '@/features/auth/services/complete-registration';
import { useDeviceID } from '@/components/providers/device-id-provider';
import {
  LoginDeviceType,
  LoginStatusUtils,
  getWeb3LoginType,
} from '@/const/login';
import { hideLoginModal } from '@/features/auth/components/login-form';
import { showModalRegisterInfo } from '@/features/auth/components/modal-register-info';
import { hideThirdPartyLoggingIn } from '@/features/auth/components/third-party-auth/third-party-logging-in';
import { useRefreshMutation } from '@/features/auth/services/refresh';
import { setFlagAtom, setRawSessionAtom } from '@/store/session';
import { setAuthToken } from '@/utils/token';
import { useLoginResult } from './use-login-result';
import { attributeUUIDAtom } from '@/store/attribute-uuid';
import { track, getDistinctId } from '@/utils/track';
import { getAuthSession } from '@/features/auth/services/api-auth';
import { ThirdLoginType } from '@/const/login';
import { clearSentryUser } from '@/utils/sentry-utils';

export function useAuthEffects() {
  const [, setUser] = useAtom(userAtom);
  const [shouldDisconnectWallet, setShouldDisconnectWallet] = useAtom(shouldDisconnectWalletAtom);
  const setSessionFlag = useSetAtom(setFlagAtom);
  const setRawSession = useSetAtom(setRawSessionAtom);
  const { loginSuccess } = useLoginResult();
  const deviceID = useDeviceID();
  const attributeUUID = useAtomValue(attributeUUIDAtom);
  const t = useTranslations('auth');

  // 断开钱包连接的函数，通过 postMessage 通知 wallet provider
  const disconnectWallet = useCallback(() => {
    window.postMessage({
      type: 'WALLET_DISCONNECT',
    }, window.location.origin);
  }, []);

  const { mutate: refreshToken } = useRefreshMutation();

  // 使用 ref 存储当前的 provider，用于埋点
  const currentProviderRef = useRef<string | undefined>(undefined);
  // 记录最后一次刷新时间，用于防抖（5秒内不重复刷新）
  const lastRefreshTimeRef = useRef<number>(0);

  // 统一的登出处理函数
  const handleClientSideLogout = useCallback((shouldDisconnectWallet = false) => {
    console.log('[Auth] handleClientSideLogout called', { shouldDisconnectWallet });
    if (shouldDisconnectWallet) {
      disconnectWallet();
    }
    // 清除 accessToken，确保下次登录能正常触发
    localStorage.removeItem('accessToken');
    setSessionFlag('logout');
    setRawSession(null, 'logout');
    // Clear Sentry user context when user logs out
    clearSentryUser();
  }, [disconnectWallet, setSessionFlag, setRawSession]);

  const onCompleteRegistrationSuccess = useCallback((data) => {
    if (data.accessToken) {
      // localStorage.setItem('accessToken', data.accessToken);
      loginSuccess(data.accessToken);
      hideLoginModal();
    }
  }, [loginSuccess]);

  const completeRegistrationConfig = useMemo(() => ({
    onSuccess: onCompleteRegistrationSuccess,
  }), [onCompleteRegistrationSuccess]);

  const { mutate: completeRegistration } = UseCompleteRegistration({
    mutationConfig: completeRegistrationConfig,
  });

  const handleThirdPartyOrWalletLoginSuccess = useCallback(async (loginData: CustomWeb3LoginRes | CustomThirdLoginRes, from?: string) => {
    if (!loginData) return;

    const { loginStatus } = loginData;
    // 使用语义化的枚举和工具函数进行状态判断
    if (LoginStatusUtils.isSuccess(loginStatus)) {
      track('login_click', {
        login_type: from || 'unknown',
      });
      // 登录成功
      setAuthToken(loginData.accessToken || '');
      setUser({ name: '', accessToken: loginData.accessToken || '' });

      // 如果是钱包登录，先断开钱包连接，然后关闭登录弹窗
      if (from === 'rainbowkit') {
        disconnectWallet();
        // 等待一小段时间确保断开连接完成，然后关闭弹窗
        setTimeout(() => {
          hideLoginModal();
        }, 100);
      } else {
        // 第三方登录直接关闭弹窗
        hideLoginModal();
      }
      return;
    }

    if (LoginStatusUtils.isBlacklisted(loginStatus)) {
      // 黑名单用户
      toast.error(t('errors.contactSupport'));
      handleClientSideLogout(true);
      return;
    }

    if (LoginStatusUtils.needResidenceSelection(loginStatus)) {
      // 新账号需要选择居住地
      const country = await showModalRegisterInfo({
        title: t('login.completeInfo'),
      });
      if (country && loginData.middleToken) {
        track('register_click', {
          register_type: from || 'unknown',
          invite_code: country?.inviteCode,
        });
        completeRegistration({
          params: {
            countryCode: country.code,
            inviteCode: country?.inviteCode,
            middleToken: loginData.middleToken,
          },
        });
      } else {
        handleClientSideLogout(from === 'rainbowkit');
      }
    }
  }, [completeRegistration, disconnectWallet, handleClientSideLogout, setUser, t]);

  const onWeb3LoginSuccess = useCallback((data) => {
    handleThirdPartyOrWalletLoginSuccess(data, 'rainbowkit');
  }, [handleThirdPartyOrWalletLoginSuccess]);

  const onWeb3LoginError = useCallback(() => {
    handleClientSideLogout(true);
  }, [handleClientSideLogout]);

  const web3LoginConfig = useMemo(() => ({
    onSuccess: onWeb3LoginSuccess,
    onError: onWeb3LoginError,
  }), [onWeb3LoginSuccess, onWeb3LoginError]);

  const { mutate: web3Login } = usePostWeb3Login({
    mutationConfig: web3LoginConfig,
  });

  const onThirdLoginSuccess = useCallback((data) => {
    // 关闭登录中组件
    hideThirdPartyLoggingIn();
    // 使用 ref 中存储的 provider 信息进行埋点
    handleThirdPartyOrWalletLoginSuccess(data, currentProviderRef.current);
  }, [handleThirdPartyOrWalletLoginSuccess]);

  const onThirdLoginError = useCallback(() => {
    // 关闭登录中组件
    hideThirdPartyLoggingIn();
    handleClientSideLogout();
  }, [handleClientSideLogout]);

  const thirdLoginConfig = useMemo(() => ({
    onSuccess: onThirdLoginSuccess,
    onError: onThirdLoginError,
  }), [onThirdLoginSuccess, onThirdLoginError]);

  const { mutate: thirdLogin } = usePostThirdLogin({
    mutationConfig: thirdLoginConfig,
  });

  // 监听postMessage，处理钱包登录和OAuth回调
  useEffect(() => {
    const accessToken = localStorage.getItem('accessToken');
    if (accessToken) {
      return;
    }

    const handleMessage = async (event: MessageEvent) => {
      // 验证消息来源
      if (event.origin !== window.location.origin || !['WALLET_AUTH_SUCCESS', 'AUTH_SESSION_READY'].includes(event.data.type)) {
        return;
      }

      // 处理钱包登录成功消息
      if (event.data.type === 'WALLET_AUTH_SUCCESS') {
        try {
          const walletAuth = event.data.walletAuth;
          // 从 message 中获取钱包信息
          const { message, signature, address, chainId, connectorName } = walletAuth || {};
          const loginType = getWeb3LoginType(connectorName);

          if (loginType && message && signature && deviceID && address) {
            web3Login({
              params: {
                publicKey: address,
                chainType: chainId ?? 1,
                deviceId: deviceID,
                deviceInfo: typeof window !== 'undefined' && typeof navigator !== 'undefined' ? window.navigator.userAgent : '',
                loginDeviceType: LoginDeviceType.web,
                loginType,
                message,
                signature,
                attributeUuid: attributeUUID || '',
              },
            });
          }
        } catch (error) {
          console.error('[Auth] Wallet login failed:', error);
        }
        return;
      }

      // 处理第三方登录的session获取
      if (event.data.type === 'AUTH_SESSION_READY') {
        try {
          // 调用后端session接口获取账号信息
          const sessionResult = await getAuthSession({ params: {} });

          if (sessionResult?.user) {
            const { user, provider } = sessionResult;

            // 只处理第三方登录
            if (provider && ['google', 'facebook', 'telegram', 'telegram-login'].includes(provider) && user?.id && deviceID) {
              let loginType: (typeof ThirdLoginType)[keyof typeof ThirdLoginType] | undefined;
              if (provider === 'google') {
                loginType = ThirdLoginType.Google;
              } else if (provider === 'facebook') {
                loginType = ThirdLoginType.Facebook;
              } else if (provider === 'telegram' || provider === 'telegram-login') {
                loginType = ThirdLoginType.Telegram;
              }

              if (loginType) {
                // 存储 provider 信息到 ref，用于埋点
                currentProviderRef.current = provider;
                thirdLogin({
                  params: {
                    signature: user.signature || '',
                    loginType,
                    deviceId: deviceID,
                    timeStamp: user.timeStamp || 0,
                    userID: user.id,
                    email: user.email || '',
                    name: user.name || '',
                    avatarURL: user.image || '',
                    attributeUuid: attributeUUID || '',
                    distinctId: getDistinctId() || undefined,
                  },
                });
              }
            }
          }
        } catch (error) {
          console.error('[Auth] Third party login failed:', error);
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [deviceID, web3Login, thirdLogin, attributeUUID]);

  useEffect(() => {
    const accessToken = localStorage.getItem('accessToken');
    if (accessToken) {
      setUser({ name: '', accessToken });

      // 使用定期检查的方式，每10分钟检查一次，只有接近1小时时才刷新
      let refreshTimeoutId: ReturnType<typeof setTimeout> | null = null;
      const CHECK_INTERVAL = 10 * 60 * 1000; // 10分钟检查一次
      const REFRESH_THRESHOLD = 55 * 60 * 1000; // 55分钟后开始刷新
      let tokenSetTime = Date.now(); // 记录token设置的时间
      let isPageVisible = !document.hidden; // 页面可见性状态

      // 添加token刷新逻辑
      const handleTokenRefresh = async () => {
        // 5秒内不重复刷新（防抖）- mutation 没有 staleTime，需要手动实现新鲜度
        const now = Date.now();
        const timeSinceLastRefresh = now - lastRefreshTimeRef.current;
        const FRESHNESS_INTERVAL = 5 * 1000; // 5秒
        if (timeSinceLastRefresh < FRESHNESS_INTERVAL) {
          return;
        }

        // 只在页面可见时执行刷新
        if (!isPageVisible) {
          return;
        }

        lastRefreshTimeRef.current = now; // 更新最后刷新时间
        refreshToken(
          undefined,
          {
            onSuccess: (data) => {
              if (data?.accessToken) {
                // 更新localStorage
                // localStorage.setItem('accessToken', data.accessToken);
                // 更新jotai状态
                setUser({ name: '', accessToken: data.accessToken });
                // 更新token设置时间
                tokenSetTime = Date.now();
              }
            },
          },
        );
      };

      // 页面可见性变化监听器
      const handleVisibilityChange = async () => {
        const wasVisible = isPageVisible;
        isPageVisible = !document.hidden;

        // 当页面从不可见变为可见时，检查是否需要立即刷新token
        if (!wasVisible && isPageVisible) {
          const now = Date.now();
          const timeSinceTokenSet = now - tokenSetTime;

          // 如果在页面不可见期间已经超过刷新阈值，立即执行刷新
          if (timeSinceTokenSet >= REFRESH_THRESHOLD) {
            await handleTokenRefresh();
          }
        }
      };

      // 初始刷新
      handleTokenRefresh();

      const scheduleNextCheck = () => {
        refreshTimeoutId = setTimeout(async () => {
          // 只在页面可见时执行定时检查
          if (isPageVisible) {
            const now = Date.now();
            const timeSinceTokenSet = now - tokenSetTime;

            // 如果已经超过55分钟，执行token刷新
            if (timeSinceTokenSet >= REFRESH_THRESHOLD) {
              await handleTokenRefresh();
              // 重置token设置时间
              tokenSetTime = now;
            }
          }

          // 继续安排下一次检查（无论页面是否可见都要安排）
          scheduleNextCheck();
        }, CHECK_INTERVAL);
      };

      // 添加页面可见性监听器
      document.addEventListener('visibilitychange', handleVisibilityChange);

      // 开始定时检查
      scheduleNextCheck();

      // 清理定时器和事件监听器
      return () => {
        if (refreshTimeoutId) {
          clearTimeout(refreshTimeoutId);
          refreshTimeoutId = null;
        }
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }
  }, [setUser, refreshToken]);


  // 监听钱包断连状态
  useEffect(() => {
    if (shouldDisconnectWallet) {
      // 执行钱包断连
      disconnectWallet();
      // 重置状态
      setShouldDisconnectWallet(false);
    }
  }, [shouldDisconnectWallet, disconnectWallet, setShouldDisconnectWallet]);
}
